/**
 * Action Queue Page V2
 * Primary task queue with interactive checklist, progress tracking
 * Uses v2 API endpoints with full schema support
 * Phase 2: Added entity filtering for cross-entity compliance
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Zap, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Filter,
  Search,
  Flag,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Play,
  Target,
  TrendingUp,
  Calendar,
  Users,
  Building2
} from 'lucide-react';

import ObligationDetailDrawerV2 from '../../components/obligations/ObligationDetailDrawerV2';
import { apiFetch } from '../../services/api_service';

export default function ActionQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [updatingItem, setUpdatingItem] = useState(null);
  // Phase 2: Entity filter state
  const [entities, setEntities] = useState([]);
  // Read entityId from URL params if present
  const [selectedEntity, setSelectedEntity] = useState(searchParams.get('entityId') || 'all');

  // Fetch entities for filter dropdown
  useEffect(() => {
    async function fetchEntities() {
      try {
        const data = await apiFetch('/v1/entities');
        setEntities(data?.entities || []);
      } catch (err) {
        console.error('Error fetching entities:', err);
      }
    }
    fetchEntities();
  }, []);

  // Update URL when entity filter changes
  const handleEntityChange = (entityId) => {
    setSelectedEntity(entityId);
    if (entityId === 'all') {
      searchParams.delete('entityId');
    } else {
      searchParams.set('entityId', entityId);
    }
    setSearchParams(searchParams);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      // Phase 2: Add entity filter
      if (selectedEntity !== 'all') params.append('entity_id', selectedEntity);
      
      const [listData, statsData] = await Promise.all([
        apiFetch(`/v1/obligations-v2/?${params}`),
        apiFetch('/v1/obligations-v2/analytics/statistics')
      ]);
      
      setObligations(listData?.items || []);
      setStatistics(statsData || {});
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, selectedEntity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateChecklistItem = async (obligationId, itemId, newStatus) => {
    setUpdatingItem(`${obligationId}-${itemId}`);
    try {
      const data = await apiFetch(
        `/v1/obligations-v2/${obligationId}/checklist/${itemId}`,
        { method: 'PUT', body: JSON.stringify({ status: newStatus }) }
      );
      if (!data?._error) {
        await fetchData();
      }
    } catch (err) {
      console.error('Error updating checklist item:', err);
    } finally {
      setUpdatingItem(null);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredObligations = obligations.filter(item => {
    const matchesSearch = 
      (item.obligation_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.obligation_code || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Group by status
  const groupedObligations = {
    overdue: filteredObligations.filter(o => o.status === 'overdue'),
    in_progress: filteredObligations.filter(o => o.status === 'in_progress'),
    not_started: filteredObligations.filter(o => o.status === 'not_started'),
    blocked: filteredObligations.filter(o => o.status === 'blocked'),
    completed: filteredObligations.filter(o => o.status === 'completed'),
  };

  const stats = statistics;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="action-queue-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <span className="ml-2 text-[#6B6B6B]">Loading action queue...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center" data-testid="action-queue-error">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="action-queue-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white">Action Queue</h1>
            {stats.overdue_count > 0 && (
              <span className="px-2.5 py-1 text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg animate-pulse">
                {stats.overdue_count} OVERDUE
              </span>
            )}
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Your tasks across all active disclosure cycles • {stats.total || 0} total obligations
          </p>
        </div>
        <button 
          onClick={fetchData}
          className="p-2.5 rounded-lg bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#6B6B6B] hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard 
          icon={AlertTriangle} 
          label="Overdue" 
          value={stats.overdue_count || 0} 
          color="red"
          subtitle="Need attention"
        />
        <StatCard 
          icon={Play} 
          label="In Progress" 
          value={stats.by_status?.in_progress || 0} 
          color="blue"
          subtitle="Active work"
        />
        <StatCard 
          icon={Clock} 
          label="Due This Week" 
          value={stats.due_this_week || 0} 
          color="amber"
          subtitle="Coming up"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Avg Progress" 
          value={`${Math.round(stats.average_progress || 0)}%`} 
          color="emerald"
          subtitle="Completion"
        />
        <StatCard 
          icon={Target} 
          label="Completed" 
          value={stats.by_status?.completed || 0} 
          color="teal"
          subtitle="Done"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search obligations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#4A4A4A] focus:border-emerald-500/50 focus:outline-none"
            data-testid="action-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          {/* Phase 2: Entity Filter */}
          {entities.length > 0 && (
            <select
              value={selectedEntity}
              onChange={(e) => handleEntityChange(e.target.value)}
              className="px-3 py-2.5 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="entity-filter-select"
            >
              <option value="all">All Entities</option>
              {entities.map(entity => (
                <option key={entity._id} value={entity._id}>
                  {entity.legalName} ({entity.jurisdiction?.country || 'N/A'})
                </option>
              ))}
            </select>
          )}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2.5 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2.5 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="overdue">Overdue</option>
            <option value="in_progress">In Progress</option>
            <option value="not_started">Not Started</option>
            <option value="blocked">Blocked</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Obligation Groups */}
      <div className="space-y-6">
        {/* Overdue Section */}
        {groupedObligations.overdue.length > 0 && (
          <ObligationSection
            title="Overdue"
            count={groupedObligations.overdue.length}
            icon={AlertTriangle}
            iconColor="text-red-400"
            bgColor="bg-red-500/5"
            borderColor="border-red-500/20"
          >
            {groupedObligations.overdue.map(obl => (
              <ObligationCard 
                key={obl.id}
                obligation={obl}
                expanded={expandedCards[obl.id]}
                onToggle={() => toggleExpanded(obl.id)}
                onSelect={() => setSelectedObligation(obl.id)}
                onUpdateChecklist={handleUpdateChecklistItem}
                updatingItem={updatingItem}
              />
            ))}
          </ObligationSection>
        )}

        {/* In Progress Section */}
        {groupedObligations.in_progress.length > 0 && (
          <ObligationSection
            title="In Progress"
            count={groupedObligations.in_progress.length}
            icon={Play}
            iconColor="text-blue-400"
            bgColor="bg-blue-500/5"
            borderColor="border-blue-500/20"
          >
            {groupedObligations.in_progress.map(obl => (
              <ObligationCard 
                key={obl.id}
                obligation={obl}
                expanded={expandedCards[obl.id]}
                onToggle={() => toggleExpanded(obl.id)}
                onSelect={() => setSelectedObligation(obl.id)}
                onUpdateChecklist={handleUpdateChecklistItem}
                updatingItem={updatingItem}
              />
            ))}
          </ObligationSection>
        )}

        {/* Not Started Section */}
        {groupedObligations.not_started.length > 0 && (
          <ObligationSection
            title="Not Started"
            count={groupedObligations.not_started.length}
            icon={Clock}
            iconColor="text-[#6B6B6B]"
          >
            {groupedObligations.not_started.map(obl => (
              <ObligationCard 
                key={obl.id}
                obligation={obl}
                expanded={expandedCards[obl.id]}
                onToggle={() => toggleExpanded(obl.id)}
                onSelect={() => setSelectedObligation(obl.id)}
                onUpdateChecklist={handleUpdateChecklistItem}
                updatingItem={updatingItem}
              />
            ))}
          </ObligationSection>
        )}

        {/* Blocked Section */}
        {groupedObligations.blocked.length > 0 && (
          <ObligationSection
            title="Blocked"
            count={groupedObligations.blocked.length}
            icon={AlertTriangle}
            iconColor="text-amber-400"
            bgColor="bg-amber-500/5"
            borderColor="border-amber-500/20"
          >
            {groupedObligations.blocked.map(obl => (
              <ObligationCard 
                key={obl.id}
                obligation={obl}
                expanded={expandedCards[obl.id]}
                onToggle={() => toggleExpanded(obl.id)}
                onSelect={() => setSelectedObligation(obl.id)}
                onUpdateChecklist={handleUpdateChecklistItem}
                updatingItem={updatingItem}
              />
            ))}
          </ObligationSection>
        )}
      </div>

      {/* Empty State */}
      {filteredObligations.length === 0 && !loading && (
        <div className="text-center py-16 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <CheckCircle2 size={56} className="mx-auto text-emerald-500/30 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">All caught up!</h3>
          <p className="text-sm text-[#6B6B6B]">
            {searchQuery || filterPriority !== 'all' || filterStatus !== 'all'
              ? 'No obligations match your current filters.' 
              : 'You have no pending obligations.'}
          </p>
        </div>
      )}

      {/* Detail Drawer */}
      <ObligationDetailDrawerV2
        obligationId={selectedObligation}
        open={!!selectedObligation}
        onClose={() => setSelectedObligation(null)}
        onUpdate={fetchData}
      />
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color, subtitle }) {
  const colors = {
    red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', icon: 'bg-red-500/20' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: 'bg-amber-500/20' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', icon: 'bg-blue-500/20' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: 'bg-emerald-500/20' },
    teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/20', text: 'text-teal-400', icon: 'bg-teal-500/20' },
  };
  const c = colors[color];

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4`}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${c.icon}`}>
          <Icon size={18} className={c.text} />
        </div>
        <div>
          <div className={`text-2xl font-bold ${c.text}`}>{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
          {subtitle && <div className="text-[10px] text-[#4A4A4A]">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

// Obligation Section Component
function ObligationSection({ title, count, icon: Icon, iconColor, bgColor, borderColor, children }) {
  return (
    <section className={`rounded-xl ${bgColor || 'bg-[#0A0A0A]'} ${borderColor ? `border ${borderColor}` : ''} p-4`}>
      <div className="flex items-center gap-3 mb-4">
        <Icon size={20} className={iconColor} />
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <span className="px-2 py-0.5 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded-full">
          {count}
        </span>
      </div>
      <div className="space-y-3">
        {children}
      </div>
    </section>
  );
}

// Obligation Card with Interactive Checklist
function ObligationCard({ obligation, expanded, onToggle, onSelect, onUpdateChecklist, updatingItem }) {
  const progress = obligation.progress?.percent_complete || 0;
  const checklistItems = obligation.progress?.checklist_items || [];
  const completedItems = checklistItems.filter(i => i.status === 'completed').length;
  
  const priorityColors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    low: 'bg-[#2F2F2F] text-[#6B6B6B] border-[#2F2F2F]',
  };

  const statusColors = {
    completed: 'bg-emerald-500',
    in_progress: 'bg-blue-500',
    overdue: 'bg-red-500',
    blocked: 'bg-amber-500',
    not_started: 'bg-[#4A4A4A]',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No deadline';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden hover:border-[#2F2F2F] transition-colors"
      data-testid={`obligation-card-${obligation.id}`}
    >
      {/* Main Card */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Expand Toggle */}
          <button 
            onClick={onToggle}
            className="mt-1 p-1 rounded hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {/* Progress Circle */}
          <div className="relative w-12 h-12 shrink-0">
            <svg className="w-12 h-12 -rotate-90">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#1F1F1F" strokeWidth="4" />
              <circle 
                cx="24" cy="24" r="20" fill="none" 
                stroke={progress === 100 ? '#10b981' : '#3b82f6'} 
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${progress * 1.256} 125.6`}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
              {Math.round(progress)}%
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 text-[10px] font-mono bg-emerald-500/20 text-emerald-400 rounded">
                {obligation.obligation_code}
              </span>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${priorityColors[obligation.priority] || priorityColors.medium}`}>
                {obligation.priority}
              </span>
              {obligation.source?.framework_code && (
                <span className="px-2 py-0.5 text-[10px] bg-[#1F1F1F] text-[#6B6B6B] rounded">
                  {obligation.source.framework_code.replace('_', '/')}
                </span>
              )}
            </div>
            <h3 
              className="text-sm font-medium text-white cursor-pointer hover:text-emerald-400 transition-colors leading-tight"
              onClick={onSelect}
            >
              {obligation.obligation_title}
            </h3>
            <div className="flex items-center gap-4 mt-2 text-xs text-[#6B6B6B]">
              <span className="flex items-center gap-1">
                <Users size={12} />
                {obligation.ownership?.owner_name || 'Unassigned'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(obligation.timeline?.due_date)}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 size={12} />
                {completedItems}/{checklistItems.length} tasks
              </span>
            </div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${statusColors[obligation.status] || statusColors.not_started}`} />
            <span className="text-xs text-[#6B6B6B] capitalize">
              {(obligation.status || 'not_started').replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 ml-[60px]">
          <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                progress === 100 ? 'bg-emerald-500' : 
                progress > 50 ? 'bg-gradient-to-r from-blue-500 to-emerald-500' :
                'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Checklist */}
      {expanded && checklistItems.length > 0 && (
        <div className="px-4 pb-4 ml-[60px]">
          <div className="pt-3 border-t border-[#1F1F1F]">
            <h4 className="text-xs font-medium text-[#6B6B6B] mb-3 uppercase tracking-wider">
              Checklist Items
            </h4>
            <div className="space-y-2">
              {checklistItems.map((item, idx) => {
                const isCompleted = item.status === 'completed';
                const isUpdating = updatingItem === `${obligation.id}-${item.id}`;
                
                return (
                  <div 
                    key={item.id || idx}
                    className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                      isCompleted 
                        ? 'bg-emerald-500/10 border border-emerald-500/20' 
                        : 'bg-[#0A0A0A] border border-[#1F1F1F] hover:border-[#2F2F2F]'
                    }`}
                  >
                    <button
                      onClick={() => onUpdateChecklist(
                        obligation.id, 
                        item.id, 
                        isCompleted ? 'not_started' : 'completed'
                      )}
                      disabled={isUpdating}
                      className="shrink-0"
                    >
                      {isUpdating ? (
                        <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                      ) : isCompleted ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-[#4A4A4A] hover:border-emerald-500 transition-colors" />
                      )}
                    </button>
                    <span className={`text-sm flex-1 ${isCompleted ? 'text-emerald-400 line-through' : 'text-white'}`}>
                      {item.title}
                    </span>
                    {item.assigned_to_name && (
                      <span className="text-xs text-[#4A4A4A]">{item.assigned_to_name}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* View Full Details Button */}
          <button
            onClick={onSelect}
            className="mt-3 w-full py-2 px-4 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] hover:text-white text-xs font-medium rounded-lg transition-colors"
          >
            View Full Details →
          </button>
        </div>
      )}
    </div>
  );
}
